import { SDKPayType, SDKStatusCode } from "@nebula-sdk/core";
import copy from "copy-to-clipboard";
import { defineComponent, onMounted, onUnmounted, reactive, ref } from "vue";
import message from "../../share/message";
import Popup from "../../share/popup";
import ServiceCopy from "./copy";
import ServiceExpire from "./expire";
import ServiceHealth from "./health";
import type { NebulaWEBGameSDKPayment } from "../../../payment";
import type { SDKPayArgs, SDKPayResult } from "@nebula-sdk/core";
import type { PropType } from "vue";

import "./index.less";

export default defineComponent({
  props: {
    service: { type: Object as PropType<NebulaWEBGameSDKPayment>, required: true },
    data: { type: Object as PropType<SDKPayArgs>, required: true },
    success: { type: Function as PropType<(result: SDKPayResult) => void>, required: true },
    error: { type: Function as PropType<(result: SDKPayResult) => void>, required: true },
  },
  setup(props) {
    const visible = ref(false);

    const state = reactive({
      playerId: "",
      playerName: "",
      serverId: "",
      serverName: "",
      orderNo: "",
      orderToken: "",
      productId: "",
      productName: "",
      productPrice: 0,
      productDesc: "",
      countdownActive: false,
      countdownTimer: -1,
      healthVisible: false,
      healthMessage: "",
      copyVisible: false,
      copyRedirectUrl: "",
      expireVisible: false,
    });

    const startOrderCountdown = () => {
      state.countdownActive = true;
      window.clearTimeout(state.countdownTimer);

      const callback = async () => {
        state.countdownActive = false;

        const { status, data } = await props.service.requestOrderQuery({
          orderNo: state.orderNo,
          orderToken: state.orderToken,
        });

        if (status === "success" && data!.status === 1) {
          props.success({
            status: "success",
            code: SDKStatusCode.Success,
            msg: "支付成功",
            data: { payType: SDKPayType.Browser, orderNo: state.orderNo },
          });
        } else {
          state.expireVisible = true;
        }
        visible.value = false;
      };

      state.countdownTimer = window.setTimeout(
        () => {
          void callback();
        },
        5 * 60 * 1000,
      );
    };

    const stopOrderCountdown = () => {
      state.countdownActive = false;
      window.clearTimeout(state.countdownTimer);
    };

    const onCancelClick = () => {
      visible.value = false;
      props.error({
        status: "error",
        code: SDKStatusCode.PayGoodsDirectCancelError,
        msg: "支付失败",
        data: null,
      });
    };

    const onAlipayClick = async () => {
      const { status, code, data } = await props.service.startAplipayPayment({
        orderNo: state.orderNo,
        orderToken: state.orderToken,
      });
      if (status === "success") {
        startOrderCountdown();
        return;
      }

      if (code === SDKStatusCode.PayNoSupportBrowserError && data?.redirectUrl) {
        state.copyRedirectUrl = data.redirectUrl;
        state.copyVisible = true;
        return;
      }

      props.error({ status: "error", code: SDKStatusCode.PayError, msg: "支付失败", data: null });
    };

    const onWechatClick = async () => {
      const { status } = await props.service.startWechatPayment({
        orderNo: state.orderNo,
        orderToken: state.orderToken,
      });
      if (status === "success") {
        startOrderCountdown();
      } else {
        props.error({ status: "error", code: SDKStatusCode.PayError, msg: "支付失败", data: null });
      }
    };

    const onFinishClick = async () => {
      const { status, data } = await props.service.requestOrderQuery({
        orderNo: state.orderNo,
        orderToken: state.orderToken,
      });

      if (status === "error") {
        props.error({ status: "error", code: SDKStatusCode.PayError, msg: "支付失败", data: null });
      }
      if (data!.status !== 1) {
        message.info({ message: "尚未完成支付，请确认支付状态" });
        return;
      }
      visible.value = false;
      props.success({
        status: "success",
        code: SDKStatusCode.Success,
        msg: "支付成功",
        data: { payType: SDKPayType.Browser, orderNo: state.orderNo },
      });
    };

    const onHealthConfirm = () => {
      state.healthVisible = false;
      state.healthMessage = "";
      visible.value = false;
      props.error({
        status: "error",
        code: SDKStatusCode.PayAntiAddictionError,
        msg: state.healthMessage,
        data: null,
      });
    };

    const onExpireConfirm = () => {
      state.expireVisible = false;
      visible.value = false;
      props.error({
        status: "error",
        code: SDKStatusCode.PayOrderExpireError,
        msg: "支付失败",
        data: null,
      });
    };

    const onCopyConfirm = async () => {
      const result = state.copyRedirectUrl ? await copy(state.copyRedirectUrl) : false;
      if (!result) {
        message.info({ message: "复制失败，请手动前往其他浏览器打开支付链接" });
        return;
      }

      state.copyVisible = false;
      startOrderCountdown();
      message.info({ message: "复制成功，请前往外部浏览器粘贴访问" });
    };

    onMounted(async () => {
      message.loading(true);
      const orderResult = await props.service.createPayOrder(props.data);
      message.loading(false);

      if (orderResult.status === "error") {
        if (orderResult.code === SDKStatusCode.PayAntiAddictionError) {
          state.healthVisible = true;
          state.healthMessage = orderResult.msg;
        } else {
          props.error(orderResult as SDKPayResult);
        }
        return;
      }

      state.playerId = orderResult.data!.playerId;
      state.playerName = orderResult.data!.playerName;

      state.serverId = orderResult.data!.serverId;
      state.serverName = orderResult.data!.serverName;

      state.orderNo = orderResult.data!.orderNo;
      state.orderToken = orderResult.data!.orderToken;

      state.productId = orderResult.data!.productId;
      state.productName = orderResult.data!.productName;
      state.productPrice = orderResult.data!.productPrice;
      state.productDesc = orderResult.data!.productDesc;

      visible.value = true;
    });

    onUnmounted(() => {
      stopOrderCountdown();
    });

    const renderOrderNode = () => {
      return (
        <div class="feiyu-pay--order">
          <div class="feiyu-pay--order-content">
            <div class="feiyu-pay--order-content-item">
              <div class="feiyu-pay--order-content-item-label">{"商品信息"}</div>
              <div class="feiyu-pay--order-content-item-content">
                <div class="feiyu-pay--order-content-item-content-name">{state.productName}</div>
                <div
                  class="feiyu-pay--order-content-item-content-summary"
                  v-show={!!state.productDesc}
                >
                  {state.productDesc}
                </div>
              </div>
            </div>
            <div class="feiyu-pay--order-content-item">
              <div class="feiyu-pay--order-content-item-label">{"充值角色"}</div>
              <div class="feiyu-pay--order-content-item-content">{`${state.playerName} | ${state.serverName}`}</div>
            </div>
            <div class="feiyu-pay--order-content-divider" />
            <div class="feiyu-pay--order-content-total">
              <div class="feiyu-pay--order-content-total-label">{"合计:"}</div>
              <div class="feiyu-pay--order-content-total-amount">{`￥${(state.productPrice / 100).toFixed(2)}`}</div>
            </div>
          </div>
          <div class="feiyu-pay--order-action">
            <div
              class="feiyu-pay--order-action-alipay"
              onClick={() => {
                void onAlipayClick();
              }}
            >
              <div class="feiyu-pay--order-action-alipay-content">{"支付宝支付"}</div>
            </div>
            <div
              class="feiyu-pay--order-action-wechat"
              onClick={() => {
                void onWechatClick();
              }}
            >
              <div class="feiyu-pay--order-action-wechat-content">{"微信支付"}</div>
            </div>
          </div>
        </div>
      );
    };

    const renderCountdownNode = () => {
      return (
        <div class="feiyu-pay--countdown">
          <div class="feiyu-pay--countdown-content">
            <div class="feiyu-pay--countdown-content-time" />
            <div class="feiyu-pay--countdown-content-title">{"订单5分钟内有效，请及时付款"}</div>
            <div class="feiyu-pay--countdown-content-desc">{"注：付款完成前，请勿关闭此页面"}</div>
          </div>
          <div class="feiyu-pay--countdown-action">
            <div class="feiyu-pay--countdown-action-retry" onClick={onCancelClick}>
              {"重新购买"}
            </div>
            <div
              class="feiyu-pay--countdown-action-finish"
              onClick={() => {
                void onFinishClick();
              }}
            >
              {"已完成支付"}
            </div>
          </div>
        </div>
      );
    };

    return () => {
      return (
        <Popup
          closeOnOverlayClick={false}
          position="bottom"
          v-model:visible={visible.value}
          width="100%"
        >
          <div class="feiyu-pay">
            <div class="feiyu-pay--header">
              <div class="feiyu-pay--header-title">{"确认订单"}</div>
              <div class="feiyu-pay--header-cancel" onClick={onCancelClick}>
                {"取消"}
              </div>
            </div>
            {state.countdownActive ? renderCountdownNode() : renderOrderNode()}
          </div>
          <ServiceHealth
            message={state.healthMessage}
            visible={state.healthVisible}
            onConfirm={onHealthConfirm}
          />
          <ServiceCopy
            visible={state.copyVisible}
            onConfirm={() => {
              void onCopyConfirm();
            }}
          />
          <ServiceExpire visible={state.expireVisible} onConfirm={onExpireConfirm} />
        </Popup>
      );
    };
  },
});
